import { useEffect, useRef, useState } from 'react';

/**
 * Creates ultra-smooth character-by-character streaming animation
 * even when content arrives in chunks from the AI SDK
 */
export const useSmoothStreaming = (
  content: string,
  isStreaming: boolean,
  charactersPerFrame = 3,
): string => {
  const [displayedContent, setDisplayedContent] = useState<string>('');
  const animationRef = useRef<number>();
  const lastContentRef = useRef<string>(content);
  const targetContentRef = useRef<string>(content);

  useEffect(() => {
    targetContentRef.current = content;

    // If not streaming, show final content immediately
    if (!isStreaming) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      setDisplayedContent(content);
      return;
    }

    // If content hasn't changed, don't restart animation
    if (content === lastContentRef.current) {
      return;
    }

    lastContentRef.current = content;

    // Start smooth character-by-character animation
    const animate = () => {
      setDisplayedContent((current) => {
        const target = targetContentRef.current;

        // If we've reached the target, stop animating
        if (current.length >= target.length) {
          return target;
        }

        // Add a few characters per frame for smooth animation
        const nextLength = Math.min(
          current.length + charactersPerFrame,
          target.length,
        );
        const nextContent = target.substring(0, nextLength);

        // Continue animation if we haven't reached the target
        if (nextLength < target.length) {
          animationRef.current = requestAnimationFrame(animate);
        }

        return nextContent;
      });
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [content, isStreaming, charactersPerFrame]);

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return displayedContent;
};
